/* @img-migrated */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Layout, Eye, Save, UploadCloud, Image as ImageIcon, Loader2, Trash2,
  ChevronUp, ChevronDown, Plus, Monitor, Smartphone, Send, RefreshCw,
  Users, Calendar, Award, Handshake, TrendingUp, Globe, Building, Star,
  Briefcase, Heart, ChevronRight, AlertCircle, CheckCircle2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL;
const ICON_OPTIONS = [
  { id: 'users',        Icon: Users },
  { id: 'calendar',     Icon: Calendar },
  { id: 'award',        Icon: Award },
  { id: 'handshake',    Icon: Handshake },
  { id: 'trending-up',  Icon: TrendingUp },
  { id: 'globe',        Icon: Globe },
  { id: 'building',     Icon: Building },
  { id: 'star',         Icon: Star },
  { id: 'briefcase',    Icon: Briefcase },
  { id: 'heart',        Icon: Heart },
];

const SECTION_LABELS = [
  { id: 'hero',             label: 'Hero / Welcome Banner' },
  { id: 'welcome',          label: 'Welcome Guide (Hero copy)' },
  { id: 'brand_essence',    label: 'Brand Essence' },
  { id: 'featured_members', label: 'Featured Member Companies' },
  { id: 'benefits',         label: 'Membership Benefits' },
  { id: 'events',           label: 'Upcoming Events' },
  { id: 'video',            label: 'YouTube Video Section' },
  { id: 'cta_footer',       label: 'Footer CTA' },
];

const AdminHomepageManager = () => {
  const [draft, setDraft] = useState(null);
  const [pristine, setPristine] = useState(null);
  const [meta, setMeta] = useState({ draft_updated_at: null, published_at: null });
  const [loading, setLoading] = useState(true);
  const [savingDraft, setSavingDraft] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop | mobile
  const previewRef = useRef(null);

  const isDirty = useMemo(() => {
    if (!draft || !pristine) return false;
    return JSON.stringify(draft) !== JSON.stringify(pristine);
  }, [draft, pristine]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/homepage`, { withCredentials: true });
      setDraft(res.data.draft);
      setPristine(res.data.draft);
      setMeta({ draft_updated_at: res.data.draft_updated_at, published_at: res.data.published_at });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load homepage content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Push the current draft into the live preview iframe so admins see changes
  // without having to save first.
  useEffect(() => {
    if (!draft || !previewRef.current?.contentWindow) return;
    try {
      previewRef.current.contentWindow.postMessage(
        { type: 'dccf-homepage-preview', content: draft },
        PUBLIC_BASE,
      );
    } catch { /* */ }
  }, [draft]);

  const updateDraft = (path, value) => {
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev || {}));
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (cur[keys[i]] == null) cur[keys[i]] = {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const saveDraft = async (silent = false) => {
    if (!draft) return;
    setSavingDraft(true);
    try {
      const res = await axios.put(`${API}/admin/homepage/draft`, { content: draft }, { withCredentials: true });
      setPristine(res.data.draft);
      setMeta((m) => ({ ...m, draft_updated_at: res.data.draft_updated_at }));
      if (!silent) toast.success('Draft saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingDraft(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      // Save first to capture latest changes, then publish.
      await axios.put(`${API}/admin/homepage/draft`, { content: draft }, { withCredentials: true });
      const res = await axios.post(`${API}/admin/homepage/publish`, {}, { withCredentials: true });
      setMeta((m) => ({ ...m, published_at: res.data.published_at }));
      setPristine(draft);
      toast.success('Published — public homepage updated');
      // Force the preview iframe to reload from the live API so it reflects published state.
      if (previewRef.current) previewRef.current.src = previewRef.current.src;  
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  if (loading || !draft) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading homepage…
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="admin-homepage-manager">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3 max-w-[1600px] mx-auto">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-[#002249] text-white flex items-center justify-center flex-shrink-0">
              <Layout className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h1 className="text-[15.5px] font-bold text-[#002249] tracking-tight">Homepage Manager</h1>
              <p className="text-[11px] text-slate-500 truncate">
                {isDirty
                  ? 'Unsaved changes · '
                  : meta.draft_updated_at ? 'Draft saved · ' : ''}
                {meta.published_at ? `Last published ${new Date(meta.published_at).toLocaleString()}` : 'Never published'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={fetchAll} className="h-9 rounded-lg" data-testid="hp-reload">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reload
            </Button>
            <Button onClick={() => saveDraft()} disabled={savingDraft || !isDirty} className="h-9 rounded-lg bg-[#002249] hover:bg-[#0a3570] text-white" data-testid="hp-save-draft">
              {savingDraft ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Save Draft
            </Button>
            <Button onClick={publish} disabled={publishing} className="h-9 rounded-lg bg-[#E63946] hover:bg-[#c02a35] text-white" data-testid="hp-publish">
              {publishing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Publish Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Two-column layout: editor left, preview right */}
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-4 p-4 sm:p-6">
        {/* Editor column */}
        <div className="space-y-4 min-w-0">
          <WelcomeEditor draft={draft} onChange={updateDraft} />
          <FeaturesEditor
            features={draft.welcome?.features || []}
            onChange={(features) => updateDraft('welcome.features', features)}
          />
          <VideoEditor draft={draft} onChange={updateDraft} />
          <SectionsVisibility draft={draft} onChange={updateDraft} />
        </div>

        {/* Preview column */}
        <div className="min-w-0">
          <div className="sticky top-[68px]">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-600">
                  <Eye className="w-3.5 h-3.5" /> Live preview
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPreviewMode('desktop')}
                    className={`h-7 px-2 rounded-md text-[11px] inline-flex items-center gap-1 ${previewMode === 'desktop' ? 'bg-[#002249] text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                    data-testid="hp-preview-desktop"
                  >
                    <Monitor className="w-3 h-3" /> Desktop
                  </button>
                  <button
                    onClick={() => setPreviewMode('mobile')}
                    className={`h-7 px-2 rounded-md text-[11px] inline-flex items-center gap-1 ${previewMode === 'mobile' ? 'bg-[#002249] text-white' : 'text-slate-500 hover:bg-slate-50'}`}
                    data-testid="hp-preview-mobile"
                  >
                    <Smartphone className="w-3 h-3" /> Mobile
                  </button>
                </div>
              </div>
              <div className="bg-slate-100 p-3 flex justify-center" style={{ height: 'calc(100vh - 160px)', minHeight: 600 }}>
                <iframe
                  ref={previewRef}
                  src={`${PUBLIC_BASE}/`}
                  title="Homepage live preview"
                  className={`bg-white shadow-lg transition-all rounded ${previewMode === 'mobile' ? 'w-[390px]' : 'w-full'} h-full`}
                  data-testid="hp-preview-frame"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Section editors
// ─────────────────────────────────────────────────────────────────────

const SectionShell = ({ title, hint, enabled, onToggleEnabled, testId, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testId}>
    <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 bg-slate-50/60">
      <div>
        <h3 className="text-[13.5px] font-bold text-[#002249]">{title}</h3>
        {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
      </div>
      {onToggleEnabled && (
        <Toggle checked={enabled} onChange={onToggleEnabled} testId={`${testId}-enabled`} />
      )}
    </div>
    <div className="p-4 space-y-3">{children}</div>
  </div>
);

const Toggle = ({ checked, onChange, testId, label }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid={testId}>
    {label && <span className="text-[12px] text-slate-700">{label}</span>}
    <span
      onClick={() => onChange(!checked)}
      className={`relative inline-block w-9 h-5 rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-slate-300'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : ''}`}
      />
    </span>
  </label>
);

const WelcomeEditor = ({ draft, onChange }) => {
  const w = draft.welcome || {};
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/homepage/upload-image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange('welcome.image_url', res.data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <SectionShell
      title="1 · Hero / Welcome content"
      hint='The "Welcome to DCCF" intro block with headline, image, and stat badge.'
      enabled={w.enabled !== false}
      onToggleEnabled={(v) => onChange('welcome.enabled', v)}
      testId="hp-section-welcome"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Small label</Label>
          <Input value={w.label || ''} onChange={(e) => onChange('welcome.label', e.target.value)} placeholder="Welcome to DCCF" data-testid="hp-welcome-label" maxLength={80} />
        </div>
        <div>
          <Label>Image (cover)</Label>
          <div className="flex items-start gap-2">
            <div className="w-16 h-12 rounded-md bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
              {w.image_url ? <Img variant="generic" src={resolveImg(w.image_url)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-slate-300" />}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="hp-welcome-image-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-10 rounded-md" data-testid="hp-welcome-upload">
              {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5 mr-1.5" />}
              {w.image_url ? 'Replace' : 'Upload'}
            </Button>
          </div>
        </div>
      </div>
      <div>
        <Label>Headline</Label>
        <Input value={w.headline || ''} onChange={(e) => onChange('welcome.headline', e.target.value)} placeholder="Your Gateway to Success in Florida's Business Community" data-testid="hp-welcome-headline" maxLength={200} />
      </div>
      <div>
        <Label>Description</Label>
        <textarea
          value={w.description || ''}
          onChange={(e) => onChange('welcome.description', e.target.value)}
          rows={3}
          maxLength={1500}
          className="w-full p-3 border border-slate-200 rounded-md text-[13px] focus:outline-none focus:ring-2 focus:ring-[#002249]/20"
          data-testid="hp-welcome-description"
        />
      </div>

      {/* Stat badge */}
      <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/40">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[12.5px] font-bold text-[#002249]">Stat badge (e.g. "500+ Active Members")</p>
          <Toggle checked={w.stat?.enabled !== false} onChange={(v) => onChange('welcome.stat.enabled', v)} testId="hp-welcome-stat-enabled" label="Show" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Number</Label>
            <Input value={w.stat?.number || ''} onChange={(e) => onChange('welcome.stat.number', e.target.value)} placeholder="500+" data-testid="hp-welcome-stat-number" maxLength={20} />
          </div>
          <div>
            <Label>Label</Label>
            <Input value={w.stat?.label || ''} onChange={(e) => onChange('welcome.stat.label', e.target.value)} placeholder="Active Members" data-testid="hp-welcome-stat-label" maxLength={60} />
          </div>
        </div>
      </div>
    </SectionShell>
  );
};

const FeaturesEditor = ({ features, onChange }) => {
  const update = (i, patch) => {
    const next = [...features];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(features.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const next = [...features];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () => onChange([
    ...features,
    { id: `f${Date.now()}`, icon: 'star', title: '', description: '', url: '', enabled: true },
  ]);

  return (
    <SectionShell
      title="2 · Action buttons / feature links"
      hint="The 3 quick-link items shown in the welcome section. Add, reorder, or hide as needed."
      testId="hp-section-features"
    >
      <div className="space-y-3" data-testid="hp-features-list">
        {features.map((f, i) => (
          <div key={f.id || i} className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`hp-feature-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="h-7 w-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center" data-testid={`hp-feature-up-${i}`}><ChevronUp className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === features.length - 1} className="h-7 w-7 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center" data-testid={`hp-feature-down-${i}`}><ChevronDown className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex items-center gap-2">
                <Toggle checked={f.enabled !== false} onChange={(v) => update(i, { enabled: v })} testId={`hp-feature-enabled-${i}`} label="Show" />
                <button type="button" onClick={() => remove(i)} className="h-7 w-7 rounded-md text-rose-600 hover:bg-rose-50 flex items-center justify-center" data-testid={`hp-feature-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label>Icon</Label>
                <div className="flex gap-1.5 flex-wrap" data-testid={`hp-feature-icons-${i}`}>
                  {ICON_OPTIONS.map((opt) => {
                    const Icon = opt.Icon;
                    const active = f.icon === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => update(i, { icon: opt.id })}
                        className={`w-8 h-8 rounded-md flex items-center justify-center border transition-colors ${active ? 'border-[#002249] bg-[#002249]/10 text-[#002249]' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                        title={opt.id}
                        data-testid={`hp-feature-icon-${i}-${opt.id}`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Link URL</Label>
                <Input value={f.url || ''} onChange={(e) => update(i, { url: e.target.value })} placeholder="/members" data-testid={`hp-feature-url-${i}`} maxLength={300} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              <div>
                <Label>Title</Label>
                <Input value={f.title || ''} onChange={(e) => update(i, { title: e.target.value })} placeholder="Explore Our Members" data-testid={`hp-feature-title-${i}`} maxLength={80} />
              </div>
              <div>
                <Label>Short description</Label>
                <Input value={f.description || ''} onChange={(e) => update(i, { description: e.target.value })} placeholder="Discover businesses..." data-testid={`hp-feature-description-${i}`} maxLength={200} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" onClick={add} className="h-9 rounded-md w-full" data-testid="hp-feature-add">
        <Plus className="w-3.5 h-3.5 mr-1.5" /> Add feature link
      </Button>
    </SectionShell>
  );
};

const VideoEditor = ({ draft, onChange }) => {
  const v = draft.video || {};
  return (
    <SectionShell
      title="3 · YouTube video section"
      hint='"The Chamber Experience" — embed a YouTube link with editable title, subtitle and CTA.'
      enabled={v.enabled !== false}
      onToggleEnabled={(en) => onChange('video.enabled', en)}
      testId="hp-section-video"
    >
      <div>
        <Label>YouTube link</Label>
        <Input value={v.youtube_url || ''} onChange={(e) => onChange('video.youtube_url', e.target.value)} placeholder="https://www.youtube.com/watch?v=..." data-testid="hp-video-url" maxLength={300} />
        <p className="text-[10.5px] text-slate-400 mt-1">Accepts youtube.com/watch?v=…, youtu.be/…, /embed/…, or a bare 11-char ID.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Small label</Label>
          <Input value={v.label || ''} onChange={(e) => onChange('video.label', e.target.value)} placeholder="The Chamber Experience" data-testid="hp-video-label" maxLength={80} />
        </div>
        <div>
          <Label>Headline</Label>
          <Input value={v.headline || ''} onChange={(e) => onChange('video.headline', e.target.value)} placeholder="Experience Our Brand" data-testid="hp-video-headline" maxLength={200} />
        </div>
      </div>
      <div>
        <Label>Description</Label>
        <Input value={v.description || ''} onChange={(e) => onChange('video.description', e.target.value)} placeholder="Discover the vision, impact..." data-testid="hp-video-description" maxLength={500} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>CTA button text</Label>
          <Input value={v.cta_text || ''} onChange={(e) => onChange('video.cta_text', e.target.value)} placeholder="Discover Our Full Story" data-testid="hp-video-cta-text" maxLength={60} />
        </div>
        <div>
          <Label>CTA link</Label>
          <Input value={v.cta_url || ''} onChange={(e) => onChange('video.cta_url', e.target.value)} placeholder="/about" data-testid="hp-video-cta-url" maxLength={300} />
        </div>
      </div>
    </SectionShell>
  );
};

const SectionsVisibility = ({ draft, onChange }) => {
  const sections = draft.sections || {};
  return (
    <SectionShell
      title="4 · Section visibility"
      hint="Toggle entire homepage sections on/off. Changes go live after Publish."
      testId="hp-section-visibility"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="hp-visibility-grid">
        {SECTION_LABELS.map((s) => (
          <div key={s.id} className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2 bg-white" data-testid={`hp-visibility-${s.id}`}>
            <span className="text-[12.5px] text-[#002249] font-semibold">{s.label}</span>
            <Toggle
              checked={sections?.[s.id]?.enabled !== false}
              onChange={(v) => onChange(`sections.${s.id}.enabled`, v)}
              testId={`hp-visibility-toggle-${s.id}`}
            />
          </div>
        ))}
      </div>
      <p className="text-[10.5px] text-slate-400 mt-2 inline-flex items-center gap-1.5">
        <AlertCircle className="w-3 h-3" /> Disabled sections are hidden from the public homepage entirely.
      </p>
    </SectionShell>
  );
};

export default AdminHomepageManager;
